import { NewDatamapRow } from "features/DataImport/components/DatamapEditor/utils/datamapEdits"
import { MAX_CODE } from "features/DataImport/components/DatamapEditor/utils/validateDatamap"
import { DatamapDoc, DatamapType, UnmappedColumnFinding } from "types/datamap.types"

/** More different whole numbers than this and a column is a count or an ID, not a category. */
export const MAX_CATEGORY_CODES = 30

/** The column holds a value no category code can be: "-99", "1.5", or a number above MAX_CODE. */
const hasNonCodes = (c: UnmappedColumnFinding): boolean =>
  c.values.some((v) => !/^\d+$/.test(v.value) || Number(v.value) > MAX_CODE)

/** A type to start from, judged from the values the check found in the column. */
export const suggestType = (column: UnmappedColumnFinding): DatamapType => {
  if (column.kind === "integer") {
    const values = column.values.map((value) => value.value)
    const codes = !column.more && !hasNonCodes(column)
    if (codes && values.length > 0 && values.every((value) => value === "0" || value === "1")) return "BOOL"
    if (codes && column.distinct <= MAX_CATEGORY_CODES) return "CAT"
    return "INT"
  }
  if (column.kind === "number") return "REAL"
  return "TEXT"
}

/** The whole-number codes found across the columns, in numeric order. */
export const wizardCodes = (columns: UnmappedColumnFinding[]): string[] => {
  const codes = new Set<string>()
  for (const column of columns)
    for (const value of column.values) if (/^\d+$/.test(value.value)) codes.add(String(Number(value.value)))
  return [...codes].sort((a, b) => Number(a) - Number(b))
}

export interface WizardInput {
  columns: string[]
  /** Label per column; a missing entry means the column name. */
  labels: Record<string, string>
  type: DatamapType
  /** Label per code, CAT only. */
  codeLabels: Record<string, string>
}

const labelOf = (input: WizardInput, column: string): string => input.labels[column] ?? column

export const buildWizardRows = (input: WizardInput): NewDatamapRow[] => {
  const values =
    input.type === "CAT"
      ? Object.entries(input.codeLabels)
          .filter(([, label]) => label.trim() !== "")
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([code, label]) => ({ code, label: label.trim() }))
      : []
  return input.columns.map((column) => ({
    label: labelOf(input, column).trim(),
    type: input.type,
    variable: column,
    fieldMetadata: "",
    values,
  }))
}

/** Why the rows can't be added yet, each reason once; empty when they can. */
export const wizardProblems = (input: WizardInput, columns: UnmappedColumnFinding[], doc: DatamapDoc): string[] => {
  const problems: string[] = []
  if (input.columns.length === 0) problems.push("Choose at least one column.")

  const existing = new Set(doc.rows.map((row) => row.label))
  const labels = input.columns.map((column) => labelOf(input, column).trim())
  labels.forEach((label, i) => {
    if (label === "") problems.push(`Column ${input.columns[i]} needs a label.`)
    else if (existing.has(label)) problems.push(`The label "${label}" is already used by another row.`)
    else if (labels.indexOf(label) !== i) problems.push(`The label "${label}" is given to two columns.`)
  })

  if (input.type === "CAT") {
    if (columns.some((column) => column.more || column.kind === "text" || column.kind === "number" || hasNonCodes(column))) {
      problems.push("A category needs whole-number codes, and not too many of them. Choose INT, REAL or TEXT instead.")
    }
    for (const code of wizardCodes(columns)) {
      if ((input.codeLabels[code] ?? "").trim() === "") problems.push(`Code ${code} is in the data and needs a label.`)
    }
    const filled = Object.values(input.codeLabels).map((label) => label.trim()).filter((label) => label !== "")
    if (new Set(filled).size !== filled.length) problems.push("Two codes have the same label.")
  }
  return [...new Set(problems)]
}
